import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './EventTransition.css';


// Global trigger + ready signal
let globalTrigger = null;
let globalReady = null;

export function useEventTransition() {
  return useCallback((path) => {
    if (globalTrigger) globalTrigger(path);
  }, []);
}

/** Call this from event pages once WebSocket is connected */
export function signalTransitionReady() {
  if (globalReady) globalReady();
}

export default function EventTransition() {
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const targetPath = useRef(null);
  const overlayRef = useRef(null);
  const readyRef = useRef(false);
  const revealTimerRef = useRef(null);
  const doneTimerRef = useRef(null);

  useEffect(() => {
    globalTrigger = (path) => {
      if (active) return;
      targetPath.current = path;
      readyRef.current = false;
      setActive(true);
    };
    return () => { globalTrigger = null; };
  }, [active]);

  // Register ready signal handler
  useEffect(() => {
    if (!active) {
      globalReady = null;
      return;
    }

    globalReady = () => {
      readyRef.current = true;
    };

    return () => { globalReady = null; };
  }, [active]);

  useEffect(() => {
    if (!active) return;

    const el = overlayRef.current;
    if (!el) return;

    el.classList.add('et-enter');

    // At 500ms: curtains closed, navigate
    const navTimer = setTimeout(() => {
      if (targetPath.current) {
        navigate(targetPath.current);
        targetPath.current = null;
      }
    }, 500);

    // Poll for ready signal — minimum 800ms hold, then reveal as soon as connected
    const minHold = 800;
    const startTime = Date.now();

    const checkReady = setInterval(() => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= minHold && readyRef.current) {
        clearInterval(checkReady);
        el.classList.add('et-reveal');
        revealTimerRef.current = setTimeout(() => {
          el.classList.remove('et-enter', 'et-reveal');
          setActive(false);
        }, 500);
      }
    }, 50);

    // Safety timeout — reveal after 5s even if not connected
    const safetyTimer = setTimeout(() => {
      clearInterval(checkReady);
      if (!el.classList.contains('et-reveal')) {
        el.classList.add('et-reveal');
        revealTimerRef.current = setTimeout(() => {
          el.classList.remove('et-enter', 'et-reveal');
          setActive(false);
        }, 500);
      }
    }, 5000);

    return () => {
      clearTimeout(navTimer);
      clearInterval(checkReady);
      clearTimeout(safetyTimer);
      clearTimeout(revealTimerRef.current);
      clearTimeout(doneTimerRef.current);
    };
  }, [active, navigate]);

  if (!active) return null;

  return (
    <div className="et-overlay" ref={overlayRef} aria-hidden="true">
      <div className="et-curtain et-curtain-top" />
      <div className="et-curtain et-curtain-bottom" />

      <div className="et-center">
        <img src="/logo.png" alt="" className="et-logo" />
      </div>
    </div>
  );
}
